import React, { FC } from 'react';
import styled from 'styled-components';

import BulletCheckIconSVG from '../../assets/icons/bullet-check-icon.svg';
import {
  CARD_BREAKPOINT,
  colors,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
} from '../constants';

import { CreditCardNameGenerator } from './CreditCardNameGenerator';
import Heading from './Heading';
import SubHeading from './SubHeading';
import { TrustPilotWidget } from './TrustPilotWidget';

interface InfoColumnProps {
  firstName: string;
  lastName: string;
}

// Styles -----------------------------------------------------------------------------------------
const Container = styled.div`
  max-width: 18.5rem;
  margin: 0 2rem 0 1.1875rem;
  display: flex;
  flex-flow: column nowrap;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    width: 100%;
    max-width: 40rem;
    margin: -1rem 0 2rem;
    justify-content: space-between;
    flex-flow: row-reverse nowrap;
  }
  @media (max-width: ${CARD_BREAKPOINT}) {
    padding: 0 2rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0;
    border: 0 solid #d8d8d8;
    border-bottom-width: 0.0625rem;
    padding: 1.375rem 1.5rem 2rem;
    flex-flow: column nowrap;
    background-color: #ffffff;
  }
`;
Container.displayName = 'CreditCardInfo';

const FeatureInfoContainer = styled.div``;
FeatureInfoContainer.displayName = 'FeatureInfoContainer';

const FeatureHeading = styled(Heading)`
  margin: 1.3125rem 0 0.5625rem;
  text-align: left;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    margin: 0;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0.9375rem 0 0;
  }
`;
FeatureHeading.displayName = 'FeatureHeading';

const FeatureList = styled.div`
  margin: 0.5rem 0;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    margin: 0.25rem 0 0;
  }
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 0.25rem 2rem 0 0;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0.4375rem 0 0.5rem;
  }
`;
FeatureList.displayName = 'FeatureList';

const FeatureSup = styled.sup`
  position: relative;
  top: 0.1875rem;
  padding: 0 0 0 0.125rem;
  font-size: 1rem;
`;
FeatureSup.displayName = 'FeatureSup';

const ListItem = styled.div`
  padding: 0.5rem 0;
  display: flex;
  align-items: flex-start;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    &:last-child {
      padding: 0.5rem 0 0;
    }
  }
`;
ListItem.displayName = 'ListItem';

const ListItemBullet = styled(BulletCheckIconSVG)`
  height: 0.875rem;
  width: 0.875rem;
  min-height: 0.875rem;
  min-width: 0.875rem;
  margin: 0.1875rem 0.75rem 0 0;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    margin: 0.1875rem 0.625rem 0 0;
  }
`;
ListItemBullet.displayName = 'ListItemBullet';

const ListItemText = styled.p`
  color: ${colors.TEXT_PRIMARY};
  line-height: 1.25rem;
`;
ListItemText.displayName = 'ListItemText';

const Sup = styled.sup`
  position: relative;
  top: 0.125rem;
  padding: 0 0 0 0.0625rem;
  font-size: 0.625rem;
`;
Sup.displayName = 'Sup';

const TrustPilotContainer = styled.div``;
TrustPilotContainer.displayName = 'TrustPilotContainer';

const DesktopInfoContainer = styled.div`
  @media (max-width: ${TABLET_BREAKPOINT}) {
    display: none;
  }
`;
DesktopInfoContainer.displayName = 'DesktopInfoContainer';

const DesktopParagraph = styled(SubHeading)`
  width: 100%;
  margin: 1.5rem 0 0;
  line-height: 1.5rem;
  text-align: left;
`;
DesktopParagraph.displayName = 'DesktopParagraph';

const HR = styled.hr`
  margin: 1.3125rem 0 2.3125rem;
  border: none;
  border-top: 0.0625rem solid #bdbdbd;
  &:last-of-type {
    margin: 2.625rem 0 2.3125rem;
  }
`;
HR.displayName = 'HR';

// Component --------------------------------------------------------------------------------------
export const CreditCardInfo: FC<InfoColumnProps> = ({
  firstName,
  lastName,
}) => (
  <Container>
    <CreditCardNameGenerator firstName={firstName} lastName={lastName} />
    <FeatureInfoContainer>
      <FeatureHeading>
        Prosper
        <FeatureSup>®</FeatureSup>
        &nbsp;Card
      </FeatureHeading>
      <FeatureList>
        <ListItem>
          <ListItemBullet />
          <ListItemText>Less than perfect credit is welcome</ListItemText>
        </ListItem>
        <ListItem>
          <ListItemBullet />
          <ListItemText>
            Widely accepted Mastercard
            <Sup>®</Sup>
          </ListItemText>
        </ListItem>
        <ListItem>
          <ListItemBullet />
          <ListItemText>No security deposit required</ListItemText>
        </ListItem>
      </FeatureList>
    </FeatureInfoContainer>
    <DesktopInfoContainer>
      <HR />
      <DesktopParagraph>
        Prosper was founded in 2005 with the mission of advancing financial
        well-being. To date, we’ve helped more than 1 million people achieve
        their financial goals.
      </DesktopParagraph>
      <DesktopParagraph>
        We’re here to give credit where credit is due - and to put you back in
        control of your finances.
      </DesktopParagraph>
      <HR />
      <TrustPilotContainer>
        <DesktopParagraph>Our customers love us:</DesktopParagraph>
        <TrustPilotWidget />
      </TrustPilotContainer>
    </DesktopInfoContainer>
  </Container>
);
